import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';

const AdContainer = styled.div`
  margin: 2rem 0;
  padding: 1rem 0;
`;

// Stand Alone Explore More Component - The only widget needed
export const TaboolaStandAloneExploreMore = () => {
  const location = useLocation();
  const containerRef = useRef(null);

  useEffect(() => {
    // Clear existing ads
    if (containerRef.current) {
      containerRef.current.innerHTML = '';
    }

    // Small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      if (window._taboola) {
        try {
          window._taboola.push({
            mode: 'rbox-only',
            container: 'taboola-stand-alone-explore-more',
            placement: 'Stand Alone Explore More',
            target_type: 'mix',
            cseg: 'subscriber'
          });
        } catch (error) {
          console.log('Taboola reload error:', error);
        }
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [location.pathname]); // Reload when route changes

  return (
    <AdContainer>
      <div id="taboola-stand-alone-explore-more" ref={containerRef}></div>
    </AdContainer>
  );
};

// Below Article Thumbnails Component - New widget for the new article page
export const TaboolaBelowArticleThumbnails = () => {
  const location = useLocation();
  const containerRef = useRef(null);

  // Function to extract unique_req from URL parameters
  const getUniqueReqFromUrl = () => {
    const urlParams = new URLSearchParams(location.search);
    return urlParams.get('unique_req');
  };

  // Function to fire beacon pixel
  const fireBeacon = (uniqueReq) => {
    if (uniqueReq) {
      const beaconUrl = `https://ytentioniau.org/pixel?unique_req=${encodeURIComponent(uniqueReq)}`;
      
      // Use sendBeacon if available, otherwise use a simple image request
      if (navigator.sendBeacon) {
        navigator.sendBeacon(beaconUrl);
      } else {
        const img = new Image();
        img.src = beaconUrl;
      }
      
      console.log('Beacon fired:', beaconUrl);
    }
  };

  // Function to check if clicked element is a Taboola ad link
  const isTaboolaAdLink = (element) => {
    // Check if element or its parents have Taboola-related classes or attributes
    let current = element;
    while (current && current !== containerRef.current) {
      // Check for common Taboola classes and attributes
      if (current.classList && (
        current.classList.contains('tbl-rbox-item') ||
        current.classList.contains('tbl-rbox-item-link') ||
        current.classList.contains('tbl-rbox-item-container') ||
        current.classList.contains('tbl-rbox-item-text') ||
        current.classList.contains('tbl-rbox-item-thumbnail') ||
        current.tagName === 'A' && current.href && current.href.includes('taboola')
      )) {
        return true;
      }
      
      // Check for data attributes commonly used by Taboola
      if (current.hasAttribute && (
        current.hasAttribute('data-item-id') ||
        current.hasAttribute('data-item-url') ||
        current.hasAttribute('data-item-title')
      )) {
        return true;
      }
      
      current = current.parentElement;
    }
    return false;
  };

  useEffect(() => {
    // Clear existing ads
    if (containerRef.current) {
      containerRef.current.innerHTML = '';
    }

    // Small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      if (window._taboola) {
        try {
          window._taboola.push({
            mode: 'alternating-thumbnails-a',
            container: 'taboola-below-article-thumbnails',
            placement: 'Below Article Thumbnails',
            target_type: 'mix'
          });
        } catch (error) {
          console.log('Taboola reload error:', error);
        }
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [location.pathname]); // Reload when route changes

  // Add click tracking for Taboola ads
  useEffect(() => {
    const handleClick = (event) => {
      const target = event.target;
      
      // Check if the clicked element is a Taboola ad link
      if (isTaboolaAdLink(target)) {
        const uniqueReq = getUniqueReqFromUrl();
        
        // Only fire beacon if we have a unique_req parameter
        if (uniqueReq) {
          fireBeacon(uniqueReq);
        } else {
          console.log('No unique_req parameter found in URL');
        }
      }
    };

    // Add event listener to container for click delegation
    if (containerRef.current) {
      containerRef.current.addEventListener('click', handleClick, true);
    }

    // Cleanup event listener
    return () => {
      if (containerRef.current) {
        containerRef.current.removeEventListener('click', handleClick, true);
      }
    };
  }, [location.search]); // Re-run when URL parameters change

  return (
    <AdContainer>
      <div id="taboola-below-article-thumbnails" ref={containerRef}></div>
    </AdContainer>
  );
};